import { memo, useState } from 'react';
import { pdfjs, Document, Page } from 'react-pdf';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-pdf/dist/esm/Page/TextLayer.css';
import _map from 'lodash/map';
import _find from 'lodash/find';
import _filter from 'lodash/filter';
import _startsWith from 'lodash/startsWith';
//Local files
import { Content, PageContainer } from './DocumentContent.styled';
import Element from 'components/Documents/Builder/Element/Element';
import ErrorWrapper from 'components/Common/ErrorWrapper/ErrorWrapper';
import { elementableTypes } from 'helpers/constants';
import { getRandomString } from 'helpers';

const { TEXT } = elementableTypes;

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`;
const options = { cMapUrl: '/cmaps/', standardFontDataUrl: '/standard_fonts/'};

const DocumentContent = ({ zoom = 1, file, pages, elements, onElements, onNewFieldType }) => {
  const [numPages, setNumPages] = useState(0);
  const [pageSize, setPageSize] = useState({ maxWidth: 0, maxHeight: 0 });
  const [activeElementId, setActiveElementId] = useState('');


  const handleClear = (e) => e.preventDefault();
  const handleDrop = (e) => {
    e.preventDefault();
    onNewFieldType(null);

    const type = e.dataTransfer.getData('text');
    const randomIdElement = `new_${getRandomString(13)}`;
    const xy = e.currentTarget.getBoundingClientRect();
    onElements({
      value: [
        ...elements.value, 
        {
          id: randomIdElement, 
          elementable_attributes: { ...type === TEXT && { text: '' } },
          elementable_type: type,
          x: Math.round(e.clientX - xy.left), 
          y: Math.round(e.clientY - xy.top),
          width: 160,
          height: 48,
          page: _find(pages, (p) => p.id === parseInt(e.currentTarget?.id)),
          _destroy: false
        }
      ],
      formatedValue: [
        ...elements.formatedValue, 
        {
          id: randomIdElement, 
          elementable: { ...type === TEXT && { script: '' }, required: true },
          elementable_type: type,
          x: Math.round(e.clientX - xy.left), 
          y: Math.round(e.clientY - xy.top),
          width: 160,
          height: 48,
          page: _find(pages, (p) => p.id === parseInt(e.currentTarget?.id)),
          _destroy: false
        }
      ]
    });
  };
  const handleElementTransform = (d, ref = null, position = null, element_id) => {
    if (d.x > pageSize.maxWidth || Math.sign(d.x) === -1) return
    
    if (d.y > pageSize.maxHeight || Math.sign(d.y) === -1) {
      const foundedElement = _find(elements.value, (w) => w.id === element_id);
      const multiplier = Math.sign(d.y) * Math.ceil(Math.abs((d.y - (Math.sign(d.y) * pageSize.maxHeight)) / pageSize.maxHeight));
      const position = { x: d.x, y: d.y - (multiplier * pageSize.maxHeight) };
      const foundedNumber = foundedElement?.page?.number;
      const foundedPage = _find(pages, (p) => p.number === (foundedNumber + multiplier));

      if (!foundedPage) return

      onElements({
        value: _map(elements.value, (w) => w.id === element_id ? { ...w, page: foundedPage, ...position } : w),
        formatedValue: _map(elements.formatedValue, (w) => w.id === element_id ? { ...w, page: foundedPage, ...position } : w)
      }); 
    } else {
      onElements({
        value: _map(elements.value, (w) => w.id === element_id ? 
        { 
          ...w, 
          ...position ? { width: ref.offsetWidth, height: ref.offsetHeight, x: position.x, y: (position.y - 8) } : { x: d.x, y: d.y } 
        } : w),
        formatedValue: _map(elements.formatedValue, (w) => w.id === element_id ? 
        { 
          ...w, 
          ...position ? { width: ref.offsetWidth, height: ref.offsetHeight, x: position.x, y: position.y } : { x: d.x, y: d.y } 
        } : w)
      }); 
    }
  };
  const handleElementableChange = (changes, element_id) => {
    onElements({
      value: _map(elements.value, (w) => w.id === element_id ? { ...w, elementable_attributes: { ...w.elementable_attributes, ...changes } } : w),
      formatedValue: _map(elements.formatedValue, (w) => w.id === element_id ? { ...w, elementable: { ...w.elementable, ...changes } } : w),
    });
  };
  const handleElementDelete = (element_id) => {  
     onElements({
      value: _startsWith(element_id, 'new_') ? _filter(elements.value, (e) => e.id !== element_id) : 
      (_map(elements.value, (e) => e.id === element_id ? { ...e, _destroy: true } : e)),
      formatedValue: _filter(elements.formatedValue,  (e) => e.id !== element_id)
    }); 
  };

  return (
    <Content>
      <Document file={file?.url} options={options} onLoadSuccess={({ numPages }) => { setNumPages(numPages)}}>
      {!!numPages && _map(pages, ({ id, number }) => 
        <PageContainer
          key={`page_${id}`}
          id={id}
          onDragOver={handleClear}
          onDrop={handleDrop}
        >
          <Page
            key={`page_${id}`}
            size='A4'
            scale={zoom}
            pageIndex={number}
            onLoadSuccess={({ height, width }) => number === 0 && setPageSize({ maxHeight: height, maxWidth: width })}
          >
            {_map(_filter(elements.formatedValue, e => e?.page.id === id), (element) =>
              <Element
                key={element.id}
                bounds={`#document_page_${id}`}
                activeElementId={activeElementId}
                onTransform={(e, d, ref, _, position) => handleElementTransform(d, ref, position, element.id)}
                onChangeElementable={(changes) => handleElementableChange(changes, element.id)}
                onDelete={() => handleElementDelete(element.id)}
                onActiveElement={setActiveElementId}
                pageSize={pageSize}
                {...element}
              />
            )}
          </Page>
        </PageContainer>
      )}
      </Document>
    </Content>
  )
}

const WrappedComponent = props => {
  return (
    <ErrorWrapper>
      <DocumentContent {...props} />
    </ErrorWrapper>
  );
};

export default memo(WrappedComponent);